import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getAutoReplies, createAutoReply, updateAutoReply, deleteAutoReply } from "@/lib/dentiumApi";
import { AutoReplyForm } from "@/components/autoreplies/AutoReplyForm";
import { AutoReplyRow } from "@/components/autoreplies/AutoReplyRow";

const errText = (e, fb) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : fb);

export default function RespuestasAutomaticasPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const { data: items = [], isLoading } = useQuery({ queryKey: ["auto-replies"], queryFn: getAutoReplies });
  const refresh = () => qc.invalidateQueries({ queryKey: ["auto-replies"] });
  const onError = (e) => toast.error(errText(e, "No se pudo guardar la respuesta automática"));

  const save = useMutation({
    mutationFn: (form) => (editing ? updateAutoReply(editing.id, form) : createAutoReply(form)),
    onSuccess: () => { toast.success(editing ? "Respuesta automática actualizada" : "Respuesta automática creada"); setOpen(false); refresh(); },
    onError,
  });
  const toggle = useMutation({ mutationFn: ({ id, active }) => updateAutoReply(id, { active }), onSuccess: refresh, onError });
  const remove = useMutation({
    mutationFn: (id) => deleteAutoReply(id),
    onSuccess: () => { toast.success("Respuesta automática eliminada"); setToDelete(null); refresh(); },
    onError,
  });

  const openForm = (item = null) => { setEditing(item); setOpen(true); };
  const activeCount = items.filter((i) => i.active).length;

  return (
    <div className="h-full overflow-y-auto" data-testid="respuestas-automaticas-page">
      <div className="p-4 sm:p-6 lg:p-8 max-w-[900px] mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl font-normal text-[#1A1A1A]">Respuestas automáticas</h1>
            <p className="text-sm text-[#8A7E70] mt-1 max-w-[560px]">
              Cuando un paciente escribe una palabra o frase clave en WhatsApp o Instagram, el bot responde con el texto exacto configurado, sin usar IA. Si no hay coincidencia, responde la IA con normalidad.
            </p>
          </div>
          <Button data-testid="auto-reply-new-btn" onClick={() => openForm()} className="h-11 bg-[#1A1A1A] hover:bg-black text-white">
            <Plus className="h-4 w-4 mr-2" /> Nueva respuesta
          </Button>
        </div>

        <div className="rounded-2xl border border-[#E7E2DA] bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-[#EDE7DE] bg-[#FAF8F5] text-xs text-[#8A7E70]" data-testid="auto-reply-count">
            {items.length} {items.length === 1 ? "respuesta" : "respuestas"} · {activeCount} {activeCount === 1 ? "activa" : "activas"}
          </div>
          {!isLoading && items.length === 0 ? (
            <div data-testid="auto-reply-empty" className="px-6 py-14 text-center">
              <Zap className="h-8 w-8 mx-auto text-[#C9BBA4] mb-3" />
              <p className="text-sm text-[#6B6157]">Aún no hay respuestas automáticas.</p>
              <p className="text-xs text-[#A79E90] mt-1">Crea la primera, por ejemplo para «precio» o «horario».</p>
            </div>
          ) : (
            <div className="divide-y divide-[#EDE7DE]" data-testid="auto-reply-list">
              {items.map((it) => (
                <AutoReplyRow key={it.id} item={it} onEdit={openForm} onDelete={setToDelete}
                  toggling={toggle.isPending} onToggle={(i, active) => toggle.mutate({ id: i.id, active })} />
              ))}
            </div>
          )}
        </div>
      </div>

      <AutoReplyForm open={open} onOpenChange={setOpen} editing={editing} pending={save.isPending} onSubmit={(f) => save.mutate(f)} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="auto-reply-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta respuesta automática?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará el disparador «{toDelete?.keywords?.join(", ")}». Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="auto-reply-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="auto-reply-delete-confirm" onClick={() => remove.mutate(toDelete.id)} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
